/**
 * InsertSuggestionModal — G4D CP3.
 *
 * Modal surface for Premium users to write a Coach `structured_suggestion`
 * into a proposal section. Rendered from `CoachPanel.jsx` when the user
 * clicks a `coach-action-insert-suggestion-btn` chip.
 *
 * Contract:
 *   - Loads current section content on mount (fresh read minimises the
 *     race between the diff being shown and the confirm-hash submitted).
 *   - Shows append preview by default; toggles to replace on request.
 *   - Runs the banned-phrase regex on the visible suggestion_text.
 *     A hit disables Insert + shows an error banner (defensive — the
 *     backend re-screens too).
 *   - Submit → `POST /api/proposals/{id}/coach/messages/{message_id}/insert`
 *     with the freshest `confirm_hash` computed client-side just before
 *     dispatch.
 *   - On 409 `section_changed_since_suggestion` → "Refresh diff" button.
 *   - On 422 `suggestion_failed_guardrail` → disable Insert + guidance.
 *   - On 200 → close + surface undo toast via `onInserted`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Loader2, RefreshCcw, ShieldAlert, X } from "lucide-react";

import { api, formatApiError } from "@/api/client";
import {
  INSERT_APPEND_LABEL,
  INSERT_CANCEL_LABEL,
  INSERT_ERROR_HINT,
  INSERT_GUARDRAIL_ERROR,
  INSERT_LOADING_SECTION,
  INSERT_MODAL_HEADER_PREFIX,
  INSERT_MODAL_KICKER,
  INSERT_MODAL_RATIONALE_LABEL,
  INSERT_MODE_DESCRIPTION_APPEND,
  INSERT_MODE_DESCRIPTION_REPLACE,
  INSERT_PREVIEW_AFTER_LABEL,
  INSERT_PREVIEW_CURRENT_LABEL,
  INSERT_REFRESH_DIFF_LABEL,
  INSERT_REPLACE_LABEL,
  INSERT_SECTION_CHANGED_MESSAGE,
  INSERT_SUBMIT_LABEL,
  INSERT_SUBMITTING_LABEL,
} from "./coach_copy";
import { computeSha256Hex, hasBannedPhrase } from "./coach_helpers";
import { sectionDisplayTitle } from "./quality_helpers";

const MODE_APPEND = "append";
const MODE_REPLACE = "replace";


export default function InsertSuggestionModal({
  proposalId,
  messageId,
  suggestion,        // {section_key, suggestion_text, rationale}
  onClose,
  onInserted,        // ({audit_id, section_key, undo_expires_at}) => void
}) {
  const [mode, setMode] = useState(MODE_APPEND);
  const [currentContent, setCurrentContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState(false); // 409 section_changed
  const [guardrailBlocked, setGuardrailBlocked] = useState(false); // 422
  const [errorHint, setErrorHint] = useState(null);
  const mountedRef = useRef(true);

  // StrictMode-safe: React 18 simulates unmount/remount in dev, which
  // would leave `mountedRef.current` stuck at `false` after the first
  // pass. Re-assert `true` on every setup pass so the guarded state
  // updates continue to fire on real mounts.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const sectionKey = suggestion?.section_key || "";
  const suggestionText = suggestion?.suggestion_text || "";
  const rationale = suggestion?.rationale || "";
  const sectionTitle = sectionDisplayTitle(sectionKey);

  // Fresh read of current section content — computes the confirm_hash
  // as close to submit time as possible.
  const loadCurrent = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setConflict(false);
    try {
      const res = await api.get(`/proposals/${proposalId}`);
      const sections = res.data?.sections || {};
      const entry = sections[sectionKey];
      let content = "";
      if (typeof entry === "string") content = entry;
      else if (entry && typeof entry === "object") content = String(entry.content || "");
      if (!mountedRef.current) return;
      setCurrentContent(content);
    } catch (err) {
      if (!mountedRef.current) return;
      setLoadError(formatApiError(err));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [proposalId, sectionKey]);

  useEffect(() => {
    if (!sectionKey) return;
    loadCurrent();
  }, [loadCurrent, sectionKey]);

  // Client-side guardrail re-screen. Belt-and-suspenders.
  const clientGuardrailHit = useMemo(
    () => hasBannedPhrase(suggestionText),
    [suggestionText],
  );

  // Compose the "after" preview.
  const afterContent = useMemo(() => {
    if (currentContent == null) return "";
    if (mode === MODE_REPLACE) return suggestionText;
    if (!currentContent) return suggestionText;
    return currentContent + "\n\n" + suggestionText;
  }, [currentContent, mode, suggestionText]);

  const insertDisabled =
    loading ||
    submitting ||
    guardrailBlocked ||
    clientGuardrailHit ||
    conflict ||
    !currentContent === false && currentContent == null;

  const submit = useCallback(async () => {
    if (currentContent == null) return;
    setSubmitting(true);
    setErrorHint(null);
    try {
      const confirmHash = await computeSha256Hex(currentContent);
      const res = await api.post(
        `/proposals/${proposalId}/coach/messages/${messageId}/insert`,
        { mode, confirm_hash: confirmHash },
      );
      if (!mountedRef.current) return;
      onInserted?.({
        audit_id: res.data.audit_id,
        section_key: res.data.section_key,
        section_title: sectionTitle,
        applied_content_hash: res.data.applied_content_hash,
        undo_expires_at: res.data.undo_expires_at,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.code === "section_changed_since_suggestion") {
        setConflict(true);
        setErrorHint(INSERT_SECTION_CHANGED_MESSAGE);
      } else if (status === 422 && detail?.code === "suggestion_failed_guardrail") {
        setGuardrailBlocked(true);
        setErrorHint(INSERT_GUARDRAIL_ERROR);
      } else {
        setErrorHint(formatApiError(err) || INSERT_ERROR_HINT);
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [
    currentContent, proposalId, messageId, mode, sectionTitle, onInserted,
  ]);

  return (
    <div
      data-testid="insert-modal-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
      style={{ background: "rgba(10,10,18,0.72)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        data-testid="insert-modal"
        className="w-full max-w-3xl rounded-3xl border p-6 flex flex-col gap-4"
        style={{
          background: "var(--v2-surface, #14141c)",
          borderColor: "var(--v2-border, rgba(148,163,184,0.20))",
        }}
      >
        <header className="flex items-start gap-3 justify-between">
          <div className="min-w-0">
            <p className="text-v2-caption uppercase tracking-[0.28em] text-v2-textMuted">
              {INSERT_MODAL_KICKER}
            </p>
            <h2
              data-testid="insert-modal-heading"
              className="mt-1 text-v2-heading-sm md:text-v2-heading-md font-semibold text-v2-text"
            >
              {INSERT_MODAL_HEADER_PREFIX} <span data-testid="insert-modal-section-title">{sectionTitle}</span>
            </h2>
          </div>
          <button
            type="button"
            data-testid="insert-modal-close"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-full w-8 h-8 text-v2-textMuted hover:text-v2-text hover:bg-v2-surfaceSubtle transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {rationale && (
          <div
            data-testid="insert-modal-rationale"
            className="rounded-2xl px-4 py-3 text-v2-body-sm leading-relaxed"
            style={{
              background: "rgba(167,139,250,0.06)",
              border: "1px solid rgba(167,139,250,0.35)",
              color: "var(--v2-text, #EDEDE8)",
            }}
          >
            <span className="text-v2-caption uppercase tracking-[0.18em] text-v2-textMuted mr-2">
              {INSERT_MODAL_RATIONALE_LABEL}
            </span>
            {rationale}
          </div>
        )}

        {clientGuardrailHit && (
          <div
            data-testid="insert-modal-guardrail-error"
            className="rounded-2xl px-4 py-3 text-v2-body-sm leading-relaxed flex items-start gap-2"
            style={{
              background: "rgba(252,165,165,0.06)",
              border: "1px solid rgba(252,165,165,0.5)",
              color: "#FCA5A5",
            }}
          >
            <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {INSERT_GUARDRAIL_ERROR}
          </div>
        )}

        <fieldset
          data-testid="insert-modal-mode-radios"
          className="rounded-2xl border border-v2-border bg-v2-surfaceSubtle/40 p-3 flex flex-col md:flex-row gap-2"
        >
          <label
            data-testid="insert-modal-mode-append"
            className={`flex-1 rounded-xl px-3 py-2 flex items-start gap-2 cursor-pointer transition-colors ${
              mode === MODE_APPEND ? "bg-v2-accentSoft/40" : "bg-transparent hover:bg-v2-surfaceSubtle"
            }`}
          >
            <input
              type="radio"
              name="insert-mode"
              value={MODE_APPEND}
              checked={mode === MODE_APPEND}
              onChange={() => setMode(MODE_APPEND)}
              className="mt-1"
            />
            <div className="min-w-0">
              <p className="text-v2-body-sm font-semibold text-v2-text">
                {INSERT_APPEND_LABEL}
              </p>
              <p className="text-v2-caption text-v2-textMuted leading-relaxed">
                {INSERT_MODE_DESCRIPTION_APPEND}
              </p>
            </div>
          </label>
          <label
            data-testid="insert-modal-mode-replace"
            className={`flex-1 rounded-xl px-3 py-2 flex items-start gap-2 cursor-pointer transition-colors ${
              mode === MODE_REPLACE ? "bg-v2-accentSoft/40" : "bg-transparent hover:bg-v2-surfaceSubtle"
            }`}
          >
            <input
              type="radio"
              name="insert-mode"
              value={MODE_REPLACE}
              checked={mode === MODE_REPLACE}
              onChange={() => setMode(MODE_REPLACE)}
              className="mt-1"
            />
            <div className="min-w-0">
              <p className="text-v2-body-sm font-semibold text-v2-text">
                {INSERT_REPLACE_LABEL}
              </p>
              <p className="text-v2-caption text-v2-textMuted leading-relaxed">
                {INSERT_MODE_DESCRIPTION_REPLACE}
              </p>
            </div>
          </label>
        </fieldset>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PreviewPane
            testId="insert-modal-current-content"
            label={INSERT_PREVIEW_CURRENT_LABEL}
            content={loading ? INSERT_LOADING_SECTION : (currentContent || "")}
            muted
          />
          <PreviewPane
            testId="insert-modal-after-content"
            label={INSERT_PREVIEW_AFTER_LABEL}
            content={afterContent}
            highlightSuffix={
              mode === MODE_REPLACE ? suggestionText :
              currentContent ? suggestionText : ""
            }
          />
        </div>

        {(errorHint || conflict) && (
          <div
            data-testid="insert-modal-error"
            className="rounded-2xl px-4 py-3 text-v2-body-sm leading-relaxed flex items-start gap-2"
            style={{
              background: "rgba(252,165,165,0.06)",
              border: "1px solid rgba(252,165,165,0.5)",
              color: "#FCA5A5",
            }}
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p>{errorHint || INSERT_ERROR_HINT}</p>
              {conflict && (
                <button
                  type="button"
                  data-testid="insert-modal-refresh-diff"
                  onClick={() => { setConflict(false); setErrorHint(null); loadCurrent(); }}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-v2-caption font-semibold"
                  style={{
                    background: "rgba(167,139,250,0.14)",
                    color: "var(--v2-accent, #5D50C8)",
                    border: "1px solid rgba(167,139,250,0.35)",
                  }}
                >
                  <RefreshCcw className="w-3.5 h-3.5" /> {INSERT_REFRESH_DIFF_LABEL}
                </button>
              )}
            </div>
          </div>
        )}

        <footer className="flex items-center justify-end gap-3 pt-2 border-t border-v2-border">
          <button
            type="button"
            data-testid="insert-modal-cancel"
            onClick={onClose}
            className="rounded-full px-4 py-2 text-v2-body-sm text-v2-textMuted hover:text-v2-text bg-transparent hover:bg-v2-surfaceSubtle border border-v2-border transition-colors"
          >
            {INSERT_CANCEL_LABEL}
          </button>
          <button
            type="button"
            data-testid="insert-modal-submit"
            onClick={submit}
            disabled={insertDisabled}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-v2-body-sm font-semibold text-v2-background bg-v2-accent hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {INSERT_SUBMITTING_LABEL}
              </>
            ) : (
              INSERT_SUBMIT_LABEL
            )}
          </button>
        </footer>

        {loadError && !loading && (
          <p
            data-testid="insert-modal-load-error"
            className="text-v2-caption text-red-400 leading-relaxed"
          >
            {loadError}
          </p>
        )}
      </div>
    </div>
  );
}


function PreviewPane({ testId, label, content, muted = false, highlightSuffix = "" }) {
  // Render the base content, then the highlighted suggestion suffix.
  // For "replace" the base is empty and the whole text is highlighted.
  const base = highlightSuffix && content.endsWith(highlightSuffix)
    ? content.slice(0, content.length - highlightSuffix.length)
    : content;
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-v2-border bg-v2-surfaceSubtle/40 p-3 flex flex-col gap-2 max-h-72 overflow-y-auto"
    >
      <p className="text-v2-caption uppercase tracking-[0.18em] text-v2-textMuted">
        {label}
      </p>
      <pre
        className={`whitespace-pre-wrap text-v2-body-sm leading-relaxed font-sans ${muted ? "text-v2-textMuted" : "text-v2-text"}`}
        style={{ margin: 0 }}
      >
        {base || (!highlightSuffix ? " " : "")}
        {highlightSuffix && (
          <span
            data-testid={`${testId}-suggestion-highlight`}
            style={{
              background: "rgba(167,139,250,0.12)",
              color: "var(--v2-accent, #5D50C8)",
              padding: "0 4px",
              borderRadius: "3px",
            }}
          >
            {highlightSuffix}
          </span>
        )}
      </pre>
    </div>
  );
}
